import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, FormControl, Grid, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SidePanel, SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import { Link } from 'react-router';
import { styled } from '@mui/material/styles';
import { BankAccountStatus, DateFormats, PaymentType } from '../../../../constants/securspace-constants';
import { requestPaymentMethods } from '../../../../paymentMethods/request/payment-method-requests';
import { calculateBookingChargeAmount } from '../../../../../views/requests/booking-requests';
import { getErrorMessageForStandardResponse } from '../../../../../util/NetworkErrorUtil';
import PaymentMethodSelect from '../modals/PaymentMethodSelect';
import AddPaymentMethodFlowManager from '../../../../paymentMethods/AddPaymentMethodFlowManager';
import RentalAgreementCheckbox from '../../../../RentalAgreementCheckbox';
import CancellationPolicy from '../../../../CancellationPolicy';
import Busy from '../../../../Busy';
import moment from 'moment';
import { useSnackbar } from '../../../../../context/snackbar-context';
import type { Booking } from '../../../../../types/Booking';
import type { Account } from '../../../../../types/Account';
import type { PaymentMethod } from '../../../../../types/PaymentMethod';

import type { CalculateCostObject } from './ReviewPaymentPanel';

const TermsOfServiceLink = styled(Link)(({ theme }) => ({
  fontSize: 'inherit',
  color: theme.palette.secondary.main,
  textDecoration: 'none',
}));

const StepLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

const ProcessingFeeNotice = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  marginTop: theme.spacing(0.5),
}));

const UnverifiedSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderLeft: `3px solid ${theme.palette.warning.main}`,
  borderRadius: '0.25rem',
}));

const UnverifiedItem = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  paddingTop: theme.spacing(0.5),
}));

const isUnverifiedMethod = (pm: PaymentMethod): boolean =>
  pm.dwollaStatus === BankAccountStatus.UNVERIFIED ||
  pm.stripeStatus === BankAccountStatus.UNVERIFIED;

const getPaymentMethodDescription = (pm: PaymentMethod): string => {
  const typeLabel = pm.type === PaymentType.CARD ? pm.cardBrand : pm.bankName;
  const nick = pm.nickName?.trim() ? `${pm.nickName}: ` : '';
  return `${nick}${typeLabel} ending in ${pm.lastFour}`;
};

interface PaymentMethodPanelProps {
  open: boolean;
  account: Account;
  booking: Booking;
  handleNext: () => void;
  setCalculateCostObject: (cost: CalculateCostObject) => void;
  selectedPaymentMethod: PaymentMethod | undefined;
  setSelectedPaymentMethod: (pm: PaymentMethod | undefined) => void;
  onClose: () => void;
}

const PaymentMethodPanel = (props: PaymentMethodPanelProps) => {
  const {
    open,
    account,
    booking,
    handleNext,
    setCalculateCostObject,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    onClose,
  } = props;

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [error, setError] = useState('');
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [reopenAfterAdd, setReopenAfterAdd] = useState(false);

  const { snackbarShowMessage } = useSnackbar();

  const loadPaymentMethods = useCallback(() => {
    setError('');
    Busy.set(true);
    requestPaymentMethods(
      account.authorityId || account.id,
      (response: PaymentMethod[]) => {
        setPaymentMethods(response);
        Busy.set(false);
        if (response.length === 0) {
          setError('No payment methods available. Please add a payment method before proceeding.');
        }
      },
      (err: unknown) => {
        Busy.set(false);
        snackbarShowMessage(
          getErrorMessageForStandardResponse(err, 'An error occurred loading payment methods.'),
          'error',
        );
      },
      true,
    );
  }, [account, snackbarShowMessage]);

  useEffect(() => {
    if (open) {
      loadPaymentMethods();
    }
  }, [open, loadPaymentMethods]);

  // When add payment method drawer closes and we flagged a reopen, reload methods and show the panel
  useEffect(() => {
    if (reopenAfterAdd && !addPaymentOpen) {
      setReopenAfterAdd(false);
      loadPaymentMethods();
    }
  }, [addPaymentOpen, reopenAfterAdd, loadPaymentMethods]);

  const handleAddPaymentMethod = () => {
    setReopenAfterAdd(true);
    setAddPaymentOpen(true);
  };

  const showPaymentReview = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!selectedPaymentMethod) {
      setError('Please select a payment method before proceeding.');
    } else if (!agreementAccepted) {
      setError('Please read and accept the agreement to book space.');
    } else {
      setError('');
      handleNext();
    }
  };

  const handleToggleAgreement = () => {
    setAgreementAccepted((prev) => !prev);
  };

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    const paymentMethodId = event.target.value;

    if (value) {
      const newSelection = paymentMethods.find((pm) => pm.id === paymentMethodId);
      setSelectedPaymentMethod(newSelection);
    }
  };

  useEffect(() => {
    if (!selectedPaymentMethod || !booking?.id) return;

    let cancelled = false;

    const data = {
      id: booking.id,
      supplierAccountId: booking.supplierAccountId,
      buyerAccountId: booking.buyerAccountId,
      locationId: booking.locationId,
      numberOfSpaces: booking.numberOfSpaces,
      startDate: moment(booking.startDate).format(DateFormats.DAY),
      endDate: moment(booking.endDate).format(DateFormats.DAY),
      frequency: booking.frequency,
      recurrences: booking.recurrences,
      durationType: booking.durationType,
      rate: booking.rate,
      assetType: booking.assetType,
      brokeredBooking: booking.brokered,
      brokeredInitialChargeAlreadyPaid: booking.brokeredInitialChargeAlreadyPaid,
      brokeredRecurringChargeAlreadyPaid: booking.brokeredRecurringChargeAlreadyPaid,
      brokeredSupplierPaidPerOccurrence: booking.brokeredSupplierPaidPerOccurrence,
      brokeredBuyerChargedPerOccurrence: booking.brokeredBuyerChargedPerOccurrence,
      brokeredSupplierOverageRatePaid: booking.brokeredSupplierOverageRatePaid,
      brokeredBuyerOverageRateCharged: booking.brokeredBuyerOverageRateCharged,
      ignoreDate: booking.ignoreDate,
      paymentMethodId: selectedPaymentMethod?.id,
    };

    calculateBookingChargeAmount(data)
      .then((res: { body: CalculateCostObject }) => {
        if (!cancelled) setCalculateCostObject(res.body);
      })
      .catch((err: unknown) => {
        if (!cancelled) snackbarShowMessage(getErrorMessageForStandardResponse(err, 'An error occurred calculating booking charges.'), 'error');
      });

    return () => {
      cancelled = true;
    };
  }, [booking, selectedPaymentMethod, setCalculateCostObject, snackbarShowMessage]);

  const handleClose = () => {
    setAgreementAccepted(false);
    setError('');
    onClose();
  };

  const verifiedMethods = useMemo(
    () => paymentMethods.filter((pm) => !isUnverifiedMethod(pm)),
    [paymentMethods],
  );
  const unverifiedMethods = useMemo(
    () => paymentMethods.filter((pm) => isUnverifiedMethod(pm)),
    [paymentMethods],
  );

  return (
    <>
      <SidePanel open={open && !addPaymentOpen} onClose={handleClose} title="Complete Booking">
        <SidePanelContent>
          <FormControl component="fieldset" fullWidth>
            <Grid container flexDirection="column" gap={2}>
              <Grid container justifyContent="space-between" alignItems="center">
                <StepLabel variant="subtitle1" sx={{ mb: 0 }}>
                  1. Select Payment Method
                </StepLabel>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddPaymentMethod}
                  sx={{ textTransform: 'none' }}
                >
                  Add New
                </Button>
              </Grid>

              <PaymentMethodSelect
                paymentMethods={verifiedMethods}
                onChange={handlePaymentMethodChange}
                value={selectedPaymentMethod?.id || ''}
              />
              {selectedPaymentMethod && selectedPaymentMethod.type === 'CARD' && (
                <ProcessingFeeNotice variant="body2">
                  <InfoOutlinedIcon fontSize="small" />
                  Additional credit card processing fee applies
                </ProcessingFeeNotice>
              )}

              {unverifiedMethods.length > 0 && (
                <UnverifiedSection>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WarningAmberIcon fontSize="small" color="warning" />
                    Pending Verification
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    The following payment methods are not yet verified and cannot be selected until verification is complete.
                  </Typography>
                  {unverifiedMethods.map((pm) => (
                    <UnverifiedItem key={pm.id} variant="body2">
                      {getPaymentMethodDescription(pm)}
                    </UnverifiedItem>
                  ))}
                </UnverifiedSection>
              )}

              <StepLabel variant="subtitle1" sx={{ mt: 1 }}>
                2. Accept{' '}
                <TermsOfServiceLink to="/terms-of-use" target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </TermsOfServiceLink>{' '}
                Agreement
              </StepLabel>
              <RentalAgreementCheckbox checked={agreementAccepted} onChange={handleToggleAgreement} required />

              <CancellationPolicy />

              {error && (
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              )}
            </Grid>
          </FormControl>
        </SidePanelContent>
        <SidePanelFooter>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleClose}
                sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={8}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={showPaymentReview}
                disabled={!selectedPaymentMethod || !agreementAccepted || paymentMethods.length === 0}
                sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
              >
                Next
              </Button>
            </Grid>
          </Grid>
        </SidePanelFooter>
      </SidePanel>

      <AddPaymentMethodFlowManager
        account={account}
        open={addPaymentOpen}
        setOpen={setAddPaymentOpen}
        loadPaymentMethods={loadPaymentMethods}
      />
    </>
  );
};

export default PaymentMethodPanel;
